<template>
  <v-form ref="form" v-model="formValid" lazy-validation>
    <v-card class="w-50 mt-3">
      <v-card-title class="pa-6">
        <div class="headline6">
          {{ $t("network.initialSetup.title") }}
        </div>
      </v-card-title>
      <v-card-text class="pa-6">
        <div class="subtitle1 mb-4">
          {{ $t("network.initialSetup.selectRegionTitle") }}
        </div>
        <div class="warning-block d-flex align-center mb-5">
          <v-icon class="mr-3 ml-4" icon="$warning"> </v-icon>
          <span class="body2 coronet-warning-darken1--text">{{
            $t("network.initialSetup.selectRegionDisclaimer")
          }}</span>
        </div>
        <v-autocomplete
          data-testid="region-select"
          class="mb-8"
          v-model="localFormValue.region"
          :items="sortedRegions"
          menu-icon="icon-arrow-down-xs"
          item-title="displayValue"
          item-value="value"
          :rules="[requiredRule]"
          :label="$t('network.initialSetup.selectRegionLabel')"
          :placeholder="$t('network.initialSetup.selectRegionPlaceholder')"
          variant="outlined"
          background-color="white"
          return-object
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props" title="">
              <span class="ml-1">{{ item.raw.displayValue }}</span>
            </v-list-item>
          </template>
        </v-autocomplete>
        <v-select
          data-testid="subnet-select"
          v-model="localFormValue.subnet"
          :items="subnetOptions"
          menu-icon="icon-arrow-down-xs"
          :rules="[required()]"
          :label="$t('network.initialSetup.selectSubnetLabel')"
          :placeholder="$t('network.initialSetup.selectSubnetPlaceholder')"
          :persistent-placeholder="false"
          :hint="$t('network.initialSetup.defaultSubnetHint')"
          persistent-hint
          variant="outlined"
          class="mt-2 mb-4"
        >
          <template #message="{ message }">
            <div class="d-flex align-center">
              <v-icon size="x-small" icon="$infoXs" class="mr-1" />
              <span>{{ message }}</span>
            </div>
          </template>
        </v-select>
        <div class="subtitle1 mb-4">
          {{ $t("modals.setupVirtualOffice.confirmInputLabel") }}
        </div>
        <v-text-field
          v-model="localFormValue.approveText"
          variant="outlined"
          :rules="matchApproveRules"
          :aria-label="$t('modals.setupVirtualOffice.confirmInputLabel')"
        />
      </v-card-text>
      <v-card-actions class="d-flex justify-end pa-6">
        <v-btn
          data-testid="confirm-btn"
          color="primary"
          rounded
          elevation="0"
          size="large"
          variant="flat"
          :ripple="false"
          :disabled="actionRestricted"
          @click="saveSettings()"
        >
          {{ $t("modals.setupVirtualOffice.actionBtn") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import isEmpty from "lodash/isEmpty";
import { useI18n } from "vue-i18n";
import { isWorkspaceNewAndInDemoMode } from "@/_helpers/subscription";
import {
  DEFAULT_VIRTUAL_OFFICE_SUBNET,
  VIRTUAL_OFFICE_SUBNET_OPTIONS,
} from "@/constants/network.ts";
import { required } from "@/_helpers/validators.ts";
import { DemoWillStartModal } from "@/constants/tutorial.ts";
import { useVirtualOfficeStore } from "@/_store/network/virtual-office.module.ts";
import type { VuetifyFormRef } from "@/types.ts";
import { openDemoModeRestrictionModal } from "@/_helpers/dialog-helpers.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

type Region = { value: string; displayValue: string };
export default defineComponent({
  emits: ["setup-submitted"],
  setup(_, { emit }) {
    const form = ref<VuetifyFormRef>();
    const virtualOfficeStore = useVirtualOfficeStore();

    const { t } = useI18n();
    const formValid = ref(true);
    const localFormValue = ref<{ region?: Region; subnet: string; approveText: string }>({
      subnet: DEFAULT_VIRTUAL_OFFICE_SUBNET,
      approveText: "",
    });
    const regions = ref<Record<string, string>>({});
    const subnetOptions = VIRTUAL_OFFICE_SUBNET_OPTIONS;

    const requiredRule = (v: Region) => (isEmpty(v) ? t("validations.required") : true);

    const matchApproveRules = [
      (v?: string) => {
        if (!v || !v.length) return t("validations.typeApprove.required");
        if (v !== t("validations.typeApprove.approve").toUpperCase()) {
          return t("validations.typeApprove.exactMatch");
        }
        return true;
      },
    ];

    const sortedRegions = computed(() => {
      return Object.entries(regions.value)
        .map(([value, displayValue]): Region => ({ value, displayValue }))
        .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    });

    const actionRestricted = computed(
      () =>
        isWorkspaceFrozenOrActionRestricted(
          RolePermissionsScope.PROTECTION,
          SubscriptionModule.SWG
        ) &&
        isWorkspaceFrozenOrActionRestricted(
          RolePermissionsScope.PROTECTION,
          SubscriptionModule.NETWORK
        )
    );

    const saveSettings = async () => {
      const validationResult = await form.value?.validate();
      if (!validationResult?.valid) return;

      if (isWorkspaceNewAndInDemoMode()) {
        return openDemoModeRestrictionModal(DemoWillStartModal.NETWORK);
      }

      const success = await virtualOfficeStore.setupVirtualOffice({
        item: { region: localFormValue.value.region!, subnet: localFormValue.value.subnet },
      });

      if (success) {
        emit("setup-submitted");
      }
    };

    onMounted(async () => {
      try {
        regions.value = await virtualOfficeStore.getVpnRegions();
      } catch (error) {
        console.error(error);
      }
    });

    return {
      required,
      formValid,
      localFormValue,
      requiredRule,
      subnetOptions,
      matchApproveRules,
      sortedRegions,
      actionRestricted,
      saveSettings,
      form,
    };
  },
});
</script>
<style scoped lang="scss"></style>
